import { Button, Menu, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { me } from '../api/gen';
import { unwrap } from '../api/client';
import { useSession } from '../lib/session';

/**
 * Which organisation the caller is acting in.
 *
 * Every request carries it as X-Org-ID and the server resolves membership from
 * it, so this is not a filter — it is the tenant boundary, and a wrong choice
 * is a 403 rather than an empty list. mindvm's multi-team login picker did this
 * at sign-in; here it is a header on every request, which is what lets someone
 * change it without signing out.
 */
export function OrgPicker() {
  const { orgId, setOrgId, signOut } = useSession();
  const { data } = useQuery({
    queryKey: ['me'],
    queryFn: () => unwrap(me()),
  });

  const memberships = data?.memberships ?? [];
  const active = memberships.find((m) => m.orgId === orgId);

  // One membership and no choice made: choose it. Making somebody pick from a
  // list of one is a step that exists only because the code did not think.
  if (!orgId && memberships.length === 1) setOrgId(memberships[0].orgId);

  return (
    <Menu>
      <Menu.Target>
        <Button variant="default" size="xs">
          {active ? active.orgName : 'Choose an organisation'}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {memberships.length === 0 && (
          <Menu.Item disabled>
            <Text size="xs" c="dimmed">
              No organisations
            </Text>
          </Menu.Item>
        )}
        {memberships.map((m) => (
          <Menu.Item key={m.orgId} onClick={() => setOrgId(m.orgId)}>
            {m.orgName}
            <Text size="xs" c="dimmed">
              {m.role}
            </Text>
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item onClick={signOut}>Sign out</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
